import { reactive } from 'vue'
import { resolveApiOrigin } from '../config/apiBase.js'

// Session-expired state shared by axios.js and SessionExpiredModal.vue.
// The backend answers an expired/invalid token on api/* with an empty 404
// (not 401), so an empty 404 is confirmed with a probe of /api/user before
// the re-login popup is shown.
export const sessionState = reactive({
  expired: false,
  renewing: false, // popup opened from the "expires soon" banner
  expiresAt: null, // ms timestamp of token_expires_at, null for tokens without expiry
})

let probeInFlight = null
let expiryTimer = null

const realToken = () => {
  const token = localStorage.getItem('auth_token')
  return token && !token.startsWith('demo_token') ? token : null
}

const onLoginPage = () => window.location.pathname === '/login'

// true = token no longer accepted, false = still valid, null = could not tell (network)
async function probeToken(token) {
  try {
    const res = await fetch(`${resolveApiOrigin()}/api/user`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    })
    if (res.ok) return false
    if (res.status === 401) return true
    if (res.status === 404) return (await res.text()).trim() === ''
    return null
  } catch {
    return null
  }
}

export function markSessionExpired() {
  if (!realToken() || onLoginPage()) return
  sessionState.expired = true
}

// Called by the axios interceptor on an empty 404. One probe at a time.
export function checkSessionAfterEmpty404() {
  const token = realToken()
  if (!token || sessionState.expired || onLoginPage()) return Promise.resolve(sessionState.expired)
  if (!probeInFlight) {
    probeInFlight = probeToken(token)
      .then(expired => {
        if (expired && realToken() === token) markSessionExpired()
        return sessionState.expired
      })
      .finally(() => { probeInFlight = null })
  }
  return probeInFlight
}

// Fires at token_expires_at (24 h tokens). Tokens without an expiry get no timer.
export function scheduleExpiryCheck() {
  clearTimeout(expiryTimer)
  const expiresAt = Date.parse(localStorage.getItem('token_expires_at') || '')
  sessionState.expiresAt = realToken() && !Number.isNaN(expiresAt) ? expiresAt : null
  if (sessionState.expiresAt === null) return
  // setTimeout caps at ~24.8 days; re-arm in steps of at most one hour.
  const wait = Math.min(Math.max(expiresAt - Date.now() + 1000, 0), 60 * 60 * 1000)
  expiryTimer = setTimeout(async () => {
    if (Date.now() < expiresAt) return scheduleExpiryCheck()
    const token = realToken()
    if (token && (await probeToken(token)) !== false) markSessionExpired()
  }, wait)
}

export function clearSessionExpired() {
  sessionState.expired = false
  sessionState.renewing = false
  scheduleExpiryCheck()
}

// Another tab logged in again: reuse its token if the server accepts it.
export async function adoptTokenFromOtherTab() {
  const token = realToken()
  if (sessionState.expired && token && (await probeToken(token)) === false) {
    clearSessionExpired()
    return true
  }
  return false
}
